import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FloatingInput } from '@components/ui/input-floating';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { LockIcon } from '@components/ui/icons';
import { useApplicationStore } from 'src/store/application';

interface AddressStepProps {
  handleNextCompatible: (e?: React.BaseSyntheticEvent) => void;
  setContainerClassName?: (className: string) => void;
  handlePrev?: (e?: React.BaseSyntheticEvent) => void;
  onValidationChange?: (isValid: boolean) => void;
  attemptedSubmit?: boolean;
  onSSNValidationChange?: (isValid: boolean) => void;
}

interface AddressData {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  ssn?: string;
}

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

export const AddressStep: React.FC<AddressStepProps> = ({
  handleNextCompatible,
  setContainerClassName,
  onValidationChange,
  attemptedSubmit,
  onSSNValidationChange,
}) => {
  const { data, setData } = useOnboardingStore();
  const { applications } = useApplicationStore();
  const firstName = useMemo(() => {
    const primary = data?.applicants?.find((a) => a.applicant === 'primary');
    return primary?.firstName || '';
  }, [data]);

  const [addressData, setAddressData] = useState<AddressData>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
  });
  const [ssn, setSSN] = useState('');
  const [errors, setErrors] = useState<Record<'zipcode' | 'address1' | 'address2' | 'city' | 'state' | 'ssn', string>>({
    zipcode: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    ssn: '',
  });
  const lastFetchedZipRef = useRef<string>('');
  const [isFetchingZip, setIsFetchingZip] = useState(false);

  // Initialize data from onboarding data or applications[0] if available
  useEffect(() => {
    // First priority: check onboarding data
    if (data?.address && typeof data.address === 'object') {
      const addr = data.address;
      if (addr.address1 || addr.city || addr.state || addr.zipcode) {
        setAddressData({
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zipcode: addr.zipcode || '',
          country: addr.country || 'US',
        });
      }
    } else if (applications && applications.length > 0) {
      // Second priority: check applications[0]
      const app = applications[0];
      console.log('app', app);

      if (app.address && typeof app.address === 'object') {
        const addr = app.address as AddressData;
        if (addr.address1 || addr.city || addr.state || addr.zipcode) {
          setAddressData({
            address1: addr.address1 || '',
            address2: addr.address2 || '',
            city: addr.city || '',
            state: addr.state || '',
            zipcode: addr.zipcode || '',
            country: addr.country || 'US',
          });
        }
      }

      // Initialize SSN
      if (app.ssn) {
        setSSN(app.ssn);
      }
    }

    // Initialize SSN from onboarding data if available
    if (data?.ssn) {
      setSSN(data.ssn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format SSN with mask ###-##-####
  const formatSSN = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSSN(e.target.value);
    setSSN(formatted);
    console.log('SSN formatted:', formatted);
  };

  // Validate SSN using RegExp: /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/
  const validateSSN = (ssnValue?: string) => {
    if (!ssnValue) return false;
    const ssnRegex = /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/;
    return ssnRegex.test(ssnValue);
  };

  const validateField = (field: keyof typeof errors, value: string) => {
    switch (field) {
      case 'zipcode':
        if (!value) {
          return 'Zip Code is required';
        }
        if (!/^\d{5}$/.test(value)) {
          return 'Zip Code must be 5 digits';
        }
        return '';
      case 'address1':
        if (!value.trim()) {
          return 'Address 1 is required';
        }
        return '';
      case 'city':
        if (!value.trim()) {
          return 'City is required';
        }
        return '';
      case 'state':
        if (!value.trim()) {
          return 'State is required';
        }
        return '';
      case 'ssn':
        if (!value) return 'SSN is required';
        if (!validateSSN(value)) return 'Please enter a valid SSN';
        return '';
      case 'address2':
        return '';
      default:
        return '';
    }
  };

  const validateAll = () => {
    const nextErrors: typeof errors = {
      zipcode: validateField('zipcode', addressData.zipcode),
      address1: validateField('address1', addressData.address1),
      address2: validateField('address2', addressData.address2),
      city: validateField('city', addressData.city),
      state: validateField('state', addressData.state),
      ssn: validateField('ssn', ssn),
    };
    setErrors(nextErrors);
    return Object.values(nextErrors).every((err) => !err);
  };

  const isFormValid = useMemo(() => {
    const address1Valid = !!addressData.address1.trim();
    const cityValid = !!addressData.city.trim();
    const stateValid = !!addressData.state.trim();
    const zipcodeValid = /^\d{5}$/.test(addressData.zipcode);
    return address1Valid && cityValid && stateValid && zipcodeValid && validateSSN(ssn);
  }, [addressData.address1, addressData.city, addressData.state, addressData.zipcode, ssn]);

  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  useEffect(() => {
    onSSNValidationChange?.(validateSSN(ssn));
  }, [ssn, onSSNValidationChange]);

  useEffect(() => {
    if (attemptedSubmit) {
      validateAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptedSubmit]);

  const setApplicationAddress = (formData: AddressData) => {
    const addressObj = {
      ...(formData?.address1?.trim() && { address1: formData.address1 }),
      ...(formData?.address2?.trim() && { address2: formData.address2 }),
      ...(formData?.city?.trim() && { city: formData.city }),
      ...(formData?.state?.trim() && { state: formData.state }),
      ...(formData?.zipcode?.trim() && { zipcode: formData.zipcode }),
      ...(formData?.country?.trim() && { country: formData.country }),
    };
    const ssnValue = validateSSN(formData.ssn) ? formData.ssn : undefined;

    // Save to onboarding store (primary source)
    setData({
      ...data,
      address: addressObj,
      ssn: ssnValue,
    });

    // Also update application store for compatibility
    const application = useApplicationStore.getState().applications[0];
    if (application) {
      application.address = {
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        city: formData.city || null,
        state: formData.state || null,
        zipcode: formData.zipcode || null,
        country: formData.country || null,
      };
      application.ssn = ssnValue || null;
      console.log('application', application);
      useApplicationStore.setState({ applications: [application] });
    }
  };

  // Save form data when SSN or address changes
  useEffect(() => {
    if (
      addressData.zipcode ||
      addressData.address1 ||
      addressData.address2 ||
      addressData.city ||
      addressData.state ||
      ssn
    ) {
      const formData = {
        address1: addressData.address1,
        address2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        zipcode: addressData.zipcode,
        country: addressData.country,
        ssn,
        ssnValid: validateSSN(ssn),
      };
      setApplicationAddress(formData);
      console.log('Form data:', formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData, ssn]);

  useEffect(() => {
    if (!addressData.zipcode) {
      lastFetchedZipRef.current = '';
    }
  }, [addressData.zipcode]);

  const handleZipChange = async (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setAddressData((prev) => ({ ...prev, zipcode: digitsOnly }));
    setErrors((prev) => ({ ...prev, zipcode: '' }));

    if (digitsOnly.length === 5 && digitsOnly !== lastFetchedZipRef.current) {
      setIsFetchingZip(true);
      try {
        const response = await fetch(`https://zip.getziptastic.com/v2/US/${digitsOnly}`);
        if (!response.ok) {
          throw new Error('Failed to fetch ZIP info');
        }
        const result = await response.json();
        setAddressData((prev) => ({
          ...prev,
          city: result.city || prev.city,
          state: result.state_short || prev.state,
          country: result.country || prev.country || 'US',
        }));
        lastFetchedZipRef.current = digitsOnly;
      } catch (error) {
        console.error('ZIP lookup error:', error);
        setErrors((prev) => ({
          ...prev,
          zipcode: 'Unable to auto-fill city and state. Please enter them manually.',
        }));
        lastFetchedZipRef.current = '';
      } finally {
        setIsFetchingZip(false);
      }
    } else if (digitsOnly.length < 5) {
      lastFetchedZipRef.current = '';
    }
  };

  const handleFieldChange = (
    field: keyof AddressData,
    value: string,
    errorKey: keyof typeof errors
  ) => {
    setAddressData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [errorKey]: '' }));
  };

  return (
    <div className="flex flex-col gap-8 w-full max-w-2xl mx-auto sm:pt-4">
      <div className="w-full flex flex-col gap-8 text-center">
        <div className="flex items-center justify-center text-[#dedcd4]">
          <LockIcon className="sm:block hidden w-40 h-40" />
        </div>
        <div className="text-lg font-black leading-tight">
          {firstName}, pop in your address when you're ready.
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-4">
            <div className="w-full sm:w-1/3">
              <FloatingInput
                id="zip"
                label="Zip Code"
                type="text"
                value={addressData.zipcode}
                onChange={(e) => handleZipChange(e.target.value)}
                maxLength={5}
                inputClass={errors.zipcode ? '!border-red-500 !border-2' : undefined}
                disabled={isFetchingZip}
              />
              {errors.zipcode && (
                <p className="text-sm text-red-500 mt-1 text-left">{errors.zipcode}</p>
              )}
            </div>
            <div className="w-full sm:w-2/3">
              <FloatingInput
                id="address1"
                label="Address 1"
                type="text"
                value={addressData.address1}
                onChange={(e) =>
                  handleFieldChange('address1', e.target.value, 'address1')
                }
                inputClass={errors.address1 ? '!border-red-500 !border-2' : undefined}
              />
              {errors.address1 && (
                <p className="text-sm text-red-500 mt-1 text-left">{errors.address1}</p>
              )}
            </div>
            <div className="w-full sm:w-2/3">
              <FloatingInput
                id="address2"
                label="Address 2"
                type="text"
                value={addressData.address2}
                onChange={(e) =>
                  handleFieldChange('address2', e.target.value, 'address2')
                }
                inputClass={errors.address2 ? '!border-red-500 !border-2' : undefined}
              />
              {errors.address2 && (
                <p className="text-sm text-red-500 mt-1 text-left">{errors.address2}</p>
              )}
            </div>
            <div className="w-full sm:w-2/3 grid grid-cols-2 gap-4">
              <div className="w-full">
                <FloatingInput
                  id="city"
                  label="City"
                  type="text"
                  value={addressData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value, 'city')}
                  inputClass={errors.city ? '!border-red-500 !border-2' : undefined}
                />
                {errors.city && (
                  <p className="text-sm text-red-500 mt-1 text-left">{errors.city}</p>
                )}
              </div>
              <div className="w-full text-left">
                <DropdownFloating
                  id="state"
                  label="State"
                  value={addressData.state}
                  onChange={(value) =>
                    handleFieldChange('state', value as string, 'state')
                  }
                  options={US_STATES}
                  inputClass={errors.state ? '!border-red-500 !border-2' : undefined}
                  error={!!errors.state}
                />
                {errors.state && (
                  <p className="text-sm text-red-500 mt-1 text-left">{errors.state}</p>
                )}
              </div>
            </div>
          </div>

        </div>
        <div className='space-y-4'>
          <div className="text-lg font-black">
            And, lastly, we need your Social Security number.
          </div>
          <div className="text-sm text-gray-600">
            This information is encrypted and NEVER sold.
          </div>

          <div className='flex flex-col items-center justify-center gap-2'>
            <div className={`w-m ${errors.ssn ? '[&_input]:border-red-500 [&_input]:border-2' : ''}`}>
              <FloatingInput
                id="ssn"
                label="Enter your SS Number"
                type="text"
                value={ssn}
                onChange={(e) => {
                  handleSSNChange(e);
                  setErrors((prev) => ({ ...prev, ssn: '' }));
                }}
                maxLength={11}
                placeholder="###-##-####"
                inputClass={errors.ssn ? '!border-red-500 !border-2' : undefined}
              />
            </div>
            {errors.ssn && (
              <p className="text-sm text-red-500 -mt-2">{errors.ssn}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
